import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useWithClient } from './useWithClient'
import { getAuthConfig, getAuthUserId } from '../../../data/auth/authSelectors'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import {
  useGetProviderChrtHighRiskTasksQuery,
  useGetProviderSelfHarmTasksQuery,
  useGetProviderTriageRiskTasksQuery,
} from '../../../providers/dashboard/data/providerTasksApi'
import { RiskAlertItem } from '../panels/RiskAlertsPanel'
import { isUnacknowledgedTask, parseTaskDate } from '../utils'

export const useRiskAlerts: () => {
  riskAlerts: RiskAlertItem[]
  isLoading: boolean
  isFetching: boolean
  isSuccess: boolean
} = () => {
  const userId = useSelector(getAuthUserId)
  const isDAEngage = useIsEngageDAProvider()
  const hideRiskAlerts = useSelector(getAuthConfig)?.hideRiskAlerts
  const queryArgs = hideRiskAlerts ? skipToken : { providerId: userId }
  const {
    data: triageRiskTasks,
    isLoading: isLoadingTriageTasks,
    isFetching: isFetchingTriageTasks,
    isSuccess: isSuccessTriageTasks,
  } = useGetProviderTriageRiskTasksQuery(isDAEngage ? queryArgs : skipToken)
  const {
    data: selfHarmTasks,
    isLoading: isLoadingSelfHarmTasks,
    isFetching: isFetchingSelfHarmTasks,
    isSuccess: isSuccessSelfHarmTasks,
  } = useGetProviderSelfHarmTasksQuery(queryArgs)
  const {
    data: chrtHighRiskTasks,
    isLoading: isLoadingChrtTasks,
    isFetching: isFetchingChrtTasks,
    isSuccess: isSuccessChrtTasks,
  } = useGetProviderChrtHighRiskTasksQuery(queryArgs)

  const unacknowledgedTriageTasks = useMemo(() => triageRiskTasks?.filter(isUnacknowledgedTask), [triageRiskTasks])
  const unacknowledgedSelfHarmTasks = useMemo(() => selfHarmTasks?.filter(isUnacknowledgedTask), [selfHarmTasks])
  const unacknowledgedChrtTasks = useMemo(() => chrtHighRiskTasks?.filter(isUnacknowledgedTask), [chrtHighRiskTasks])

  const {
    items: triageAlerts,
    isLoading: isLoadingTriageClients,
    isFetching: isFetchingTriageClients,
    isSuccess: isSuccessTriageClients,
  } = useWithClient(unacknowledgedTriageTasks, (task) => task.arguments?.patient_id ?? null)
  const {
    items: selfHarmAlerts,
    isLoading: isLoadingSelfHarmClients,
    isFetching: isFetchingSelfHarmClients,
    isSuccess: isSuccessSelfHarmClients,
  } = useWithClient(unacknowledgedSelfHarmTasks, (task) => task.arguments?.patient_id ?? null)
  const {
    items: chrtAlerts,
    isLoading: isLoadingChrtClients,
    isFetching: isFetchingChrtClients,
    isSuccess: isSuccessChrtClients,
  } = useWithClient(unacknowledgedChrtTasks, (task) => task.arguments?.patient_id ?? null)

  const riskAlerts = useMemo(() => {
    const triageItems: RiskAlertItem[] = triageAlerts.map((task) => ({
      client: task.client,
      date: parseTaskDate(task.arguments?.event_date, task.arguments?.event_time),
      taskId: task.id,
      taskType: task.type,
      taskArguments: task.arguments,
    }))

    const selfHarmItems: RiskAlertItem[] = selfHarmAlerts.map((task) => ({
      client: task.client,
      date: parseTaskDate(task.arguments?.self_harm_date, task.arguments?.self_harm_time),
      taskId: task.id,
      taskType: task.type,
      taskArguments: task.arguments,
      resourceId: task.resource_id,
    }))

    const chrtItems: RiskAlertItem[] = chrtAlerts.map((task) => ({
      client: task.client,
      date: parseTaskDate(task.arguments?.event_date, task.arguments?.event_time),
      taskId: task.id,
      taskType: task.type,
    }))

    return [...triageItems, ...selfHarmItems, ...chrtItems]
  }, [triageAlerts, selfHarmAlerts, chrtAlerts])

  if (hideRiskAlerts) {
    return { riskAlerts: [], isLoading: false, isFetching: false, isSuccess: true }
  }

  return {
    riskAlerts,
    isLoading:
      isLoadingTriageTasks ||
      isLoadingSelfHarmTasks ||
      isLoadingChrtTasks ||
      isLoadingTriageClients ||
      isLoadingSelfHarmClients ||
      isLoadingChrtClients,
    isFetching:
      isFetchingTriageTasks ||
      isFetchingSelfHarmTasks ||
      isFetchingChrtTasks ||
      isFetchingTriageClients ||
      isFetchingSelfHarmClients ||
      isFetchingChrtClients,
    isSuccess:
      (!isDAEngage || isSuccessTriageTasks) &&
      isSuccessSelfHarmTasks &&
      isSuccessChrtTasks &&
      isSuccessTriageClients &&
      isSuccessSelfHarmClients &&
      isSuccessChrtClients,
  }
}
